/**
 * Source unique de vérité sur « qui est connecté ».
 *
 * La session vit dans un cookie `HttpOnly` posé par le backend : le frontend ne
 * la stocke jamais, il se contente de demander `GET /auth/moi`. Conséquence
 * pratique : fermer l’onglet ne déconnecte pas, et un 401 rencontré n’importe
 * où invalide immédiatement la session affichée (`surSessionExpiree`).
 */

import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, api, surSessionExpiree } from '../lib/api'
import { ContexteAuth, type ValeurAuth } from './contexte'
import { lireSession, type Session } from './types'

/** Clé de cache de la session courante. */
export const CLE_SESSION = ['auth', 'moi'] as const

/**
 * `null` plutôt qu’une erreur quand personne n’est connecté. Sur l’apex de la
 * vitrine, le résolveur de théâtre répond TENANT_INCONNU avant même la route
 * d’authentification : cette réponse signifie aussi « aucun compte ici ».
 *
 * Reçoit le `signal` de TanStack Query : c’est ce qui rend `cancelQueries()`
 * réellement efficace (voir `appliquer` plus bas) — sans lui, la requête
 * partirait au bout malgré l’annulation.
 */
async function chargerSession({ signal }: { signal?: AbortSignal } = {}): Promise<Session | null> {
  try {
    return lireSession(await api.get<unknown>('/auth/moi', { signal }))
  } catch (erreur) {
    if (
      erreur instanceof ApiError &&
      (erreur.estNonAuthentifie ||
        erreur.estInterdit ||
        (erreur.estIntrouvable && erreur.codeBrut === 'TENANT_INCONNU'))
    ) {
      return null
    }
    throw erreur
  }
}

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const client = useQueryClient()
  const sessionPrecedente = useRef<Session | null>(null)

  const requete = useQuery({
    queryKey: CLE_SESSION,
    queryFn: chargerSession,
    // Un administrateur peut retirer l'accès depuis un autre poste.
    staleTime: 0,
    // Une session connectée doit être révoquée rapidement. Un visiteur sans
    // session n'a rien à surveiller : éviter une requête toutes les 15 s sur
    // la vitrine et les autres pages publiques.
    // Une erreur initiale laisse `data` à undefined. Elle ne doit pas lancer
    // un minuteur qui remplace la vitrine par un écran de chargement.
    refetchInterval: (query) => (query.state.data ? 15_000 : false),
    retry: false,
    refetchOnWindowFocus: true,
  })

  // Un 401 sur n’importe quelle requête = la session a expiré côté serveur
  // (12 h pour le staff, 30 j pour un abonné). On le reflète tout de suite.
  const oublierSession = useCallback(() => {
    void client.cancelQueries()
    client.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' })
    client.getMutationCache().clear()
    client.setQueryData(CLE_SESSION, null)
  }, [client])

  useEffect(() => surSessionExpiree(oublierSession), [oublierSession])

  useEffect(() => {
    const canal =
      typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('ticket-session')
    if (canal !== null) canal.onmessage = () => oublierSession()
    return () => canal?.close()
  }, [oublierSession])

  useEffect(() => {
    if (requete.data === null && sessionPrecedente.current !== null) {
      // Une révocation détectée par /auth/moi supprime aussi les données métier.
      client.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' })
    }
    sessionPrecedente.current = requete.data ?? null
  }, [client, requete.data])

  const rafraichir = useCallback(async () => {
    await client.invalidateQueries({ queryKey: CLE_SESSION })
  }, [client])

  const appliquer = useCallback(
    async (reponse: unknown): Promise<Session> => {
      const session = lireSession(reponse)
      if (session !== null) {
        /**
         * ★ Annuler AVANT d’écrire — sinon la connexion se défait toute seule.
         *
         * Au chargement de la page, `AuthProvider` lance `GET /auth/moi`.
         * Sur l’écran de vérification du lien magique, la connexion part dans
         * la foulée : les deux requêtes sont en vol en même temps. Quand la
         * connexion répond la première — ce qui arrive dès que la machine est
         * chargée — le **401 parfaitement normal** de `/auth/moi` (il n’y avait
         * pas encore de session) se pose APRÈS notre écriture et remet la
         * session à `null`. L’abonné se retrouve renvoyé à l’écran de connexion
         * une seconde après s’y être connecté, sans qu’aucune erreur ne
         * s’affiche.
         *
         * Ce n’est pas une bizarrerie de test : c’est exactement ce qui arrive
         * à un abonné sur un téléphone lent. `cancelQueries` interrompt la
         * lecture en vol et fait ignorer son résultat ; l’écriture qui suit est
         * alors la dernière à parler.
         */
        await client.cancelQueries({ queryKey: CLE_SESSION })
        client.setQueryData(CLE_SESSION, session)
        return session
      }
      // Le backend n’a pas renvoyé la session dans la réponse : on la relit.
      const relue = await client.fetchQuery({ queryKey: CLE_SESSION, queryFn: chargerSession })
      if (relue === null) {
        throw new ApiError('NON_AUTORISE', 'Connexion impossible.', 401)
      }
      return relue
    },
    [client],
  )

  const connecterStaff = useCallback(
    async (email: string, motDePasse: string): Promise<Session> => {
      const reponse = await api.post<unknown>('/auth/login', { email, motDePasse })
      return appliquer(reponse)
    },
    [appliquer],
  )

  const demanderLienMagique = useCallback(async (email: string): Promise<void> => {
    await api.post<unknown>('/auth/magic-link', { email })
  }, [])

  const verifierLienMagique = useCallback(
    async (jeton: string): Promise<Session> => {
      const reponse = await api.post<unknown>('/auth/magic-link/verifier', { token: jeton })
      return appliquer(reponse)
    },
    [appliquer],
  )

  const deconnecter = useCallback(async (): Promise<void> => {
    try {
      await api.post<unknown>('/auth/logout')
    } finally {
      // Même si l’appel échoue, on nettoie : le poste du guichet est partagé,
      // les données du précédent utilisateur ne doivent pas rester en cache.
      oublierSession()
      if (typeof BroadcastChannel !== 'undefined') {
        const canal = new BroadcastChannel('ticket-session')
        canal.postMessage('deconnexion')
        canal.close()
      }
    }
  }, [oublierSession])

  const valeur = useMemo<ValeurAuth>(
    () => ({
      session: requete.data ?? null,
      chargement: requete.isPending,
      connecterStaff,
      demanderLienMagique,
      verifierLienMagique,
      deconnecter,
      rafraichir,
    }),
    [
      requete.data,
      requete.isPending,
      connecterStaff,
      demanderLienMagique,
      verifierLienMagique,
      deconnecter,
      rafraichir,
    ],
  )

  return <ContexteAuth value={valeur}>{children}</ContexteAuth>
}
